import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  addDoc,
  arrayUnion,
  collection,
  collectionData,
  doc,
  getDoc,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from '@angular/fire/firestore';
import { Observable, from, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Invoice } from '@shared/models/invoice/invoice.model';
import { ApprovalStateType } from '../models/enums/approval-state.enum';
import { InvoiceChatMessage, InvoiceChatMessageReply } from '../models/invoice-chat-message.model';
import { InvoiceChatThread } from '../models/invoice-chat-thread.model';

const CHAT_ELIGIBLE_STATES: Array<ApprovalStateType | string> = [
  ApprovalStateType.PENDING_APPROVAL,
  ApprovalStateType.CUSTOMER_PENDING_APPROVAL,
  ApprovalStateType.INTERNALLY_APPROVED,
  ApprovalStateType.CUSTOMER_APPROVED,
  ApprovalStateType.INTERNALLY_REJECTED,
  ApprovalStateType.CUSTOMER_REJECTED,
  ApprovalStateType.DISPUTED,
];

@Injectable({ providedIn: 'root' })
export class InvoiceChatService {
  private readonly firestore = inject(Firestore);

  canChat(invoice: Invoice | null | undefined, currentOrgId: string | null | undefined): boolean {
    if (!invoice || !currentOrgId) {
      return false;
    }
    const supplier = invoice.serviceProvider;
    const customer = invoice.createdTo;
    const isParticipant = currentOrgId === supplier || currentOrgId === customer;
    const stateAllowed = CHAT_ELIGIBLE_STATES.includes(invoice.approvalState as ApprovalStateType);
    return isParticipant && stateAllowed && !!this.resolveInvoiceId(invoice);
  }

  resolveInvoiceId(invoice: Invoice | null | undefined): string | null {
    if (!invoice) {
      return null;
    }
    // eslint-disable-next-line no-underscore-dangle
    return (invoice._id as string) || null;
  }

  ensureThread(
    invoice: Invoice,
    currentOrgUserId: string,
  ): Promise<void> {
    const invoiceId = this.resolveInvoiceId(invoice);
    if (!invoiceId) {
      return Promise.resolve();
    }
    const ref = doc(this.firestore, 'invoiceChats', invoiceId);
    return getDoc(ref).then(snap => {
      if (snap.exists()) {
        return;
      }
      const thread: any = {
        invoiceId,
        invoiceRefNo: invoice.invoiceRefNo || '',
        participants: [invoice.serviceProvider, invoice.createdTo].filter(Boolean),
        supplierOrgId: invoice.serviceProvider || '',
        customerOrgId: invoice.createdTo || '',
        createdAt: serverTimestamp(),
        createdBy: currentOrgUserId,
        lastMessage: '',
        lastMessageAt: null,
      };
      return setDoc(ref, thread);
    });
  }

  listenToMessages(invoiceId: string): Observable<InvoiceChatMessage[]> {
    if (!invoiceId) {
      return of([]);
    }
    const messagesRef = collection(this.firestore, 'invoiceChats', invoiceId, 'messages');
    const messagesQuery = query(messagesRef, orderBy('createdAt', 'asc'));
    return collectionData(messagesQuery, { idField: 'id' }).pipe(
      map((rows: any[]) => rows.map(row => this.toMessage(row, invoiceId))),
    );
  }

  sendMessage(
    invoice: Invoice,
    text: string,
    currentOrgUserId: string,
    currentOrgId: string,
    senderName: string,
    replyTo?: InvoiceChatMessageReply | null,
  ): Promise<void> {
    const invoiceId = this.resolveInvoiceId(invoice);
    if (!invoiceId || !text || !text.trim()) {
      return Promise.resolve();
    }
    const trimmed = text.trim();
    return this.ensureThread(invoice, currentOrgUserId).then(() => {
      const messagesRef = collection(this.firestore, 'invoiceChats', invoiceId, 'messages');
      const payload: any = {
        invoiceId,
        text: trimmed,
        sentByOrgUserId: currentOrgUserId,
        sentByOrgId: currentOrgId,
        sentByName: senderName || 'Unknown',
        createdAt: serverTimestamp(),
        readBy: [currentOrgUserId],
      };
      if (replyTo && replyTo.messageId) {
        payload.replyTo = {
          messageId: replyTo.messageId,
          senderName: replyTo.senderName || '',
          text: (replyTo.text || '').slice(0, 280),
        };
      }
      return addDoc(messagesRef, payload).then(() => {
        const threadRef = doc(this.firestore, 'invoiceChats', invoiceId);
        return updateDoc(threadRef, {
          lastMessage: trimmed,
          lastMessageAt: serverTimestamp(),
          lastMessageBy: currentOrgUserId,
        }).catch(() => undefined);
      });
    }).then(() => undefined);
  }

  markAsRead(invoiceId: string, messageIds: string[], currentOrgUserId: string): Observable<void> {
    if (!invoiceId || !currentOrgUserId || !messageIds?.length) {
      return of(undefined);
    }
    const batch = writeBatch(this.firestore);
    messageIds.forEach(id => {
      const ref = doc(this.firestore, 'invoiceChats', invoiceId, 'messages', id);
      batch.update(ref, { readBy: arrayUnion(currentOrgUserId) });
    });
    return from(batch.commit().catch(() => undefined)).pipe(map(() => undefined));
  }

  getThread(invoiceId: string): Observable<InvoiceChatThread | null> {
    if (!invoiceId) {
      return of(null);
    }
    const ref = doc(this.firestore, 'invoiceChats', invoiceId);
    return from(getDoc(ref)).pipe(
      map(snap => {
        if (!snap.exists()) {
          return null;
        }
        const data = snap.data() as any;
        return {
          invoiceId,
          invoiceRefNo: data.invoiceRefNo,
          participants: data.participants || [],
          supplierOrgId: data.supplierOrgId,
          customerOrgId: data.customerOrgId,
          createdAt: this.toDate(data.createdAt),
          lastMessage: data.lastMessage,
          lastMessageAt: this.toDate(data.lastMessageAt),
          lastMessageBy: data.lastMessageBy,
        } as InvoiceChatThread;
      }),
    );
  }

  private toMessage(row: any, invoiceId: string): InvoiceChatMessage {
    return {
      id: row.id,
      invoiceId,
      text: row.text || '',
      sentByOrgUserId: row.sentByOrgUserId || '',
      sentByOrgId: row.sentByOrgId || '',
      sentByName: row.sentByName || 'Unknown',
      createdAt: this.toDate(row.createdAt),
      readBy: row.readBy || [],
      replyTo: row.replyTo
        ? {
            messageId: row.replyTo.messageId || '',
            senderName: row.replyTo.senderName || '',
            text: row.replyTo.text || '',
          }
        : null,
    };
  }

  private toDate(value: any): Date | null {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      return value;
    }
    if (typeof value.toMillis === 'function') {
      return new Date(value.toMillis());
    }
    if (typeof value.seconds === 'number') {
      return new Date(value.seconds * 1000);
    }
    return null;
  }
}
